/**
 * Pledge Page
 *
 * Allows donors to record a pledge for a specific cause.
 * - Donation type buttons at the top (filtered by showPledge = 1)
 * - Form pre-filled with donor info when authenticated
 */

import { useState, useEffect } from 'react';
import { useCompany } from '@/contexts/CompanyContext';
import { useAuth } from '@/contexts/AuthContext';
import { getDonationTypes } from '@/services/api/donationTypeService';
import { createPledge } from '@/services/api/pledgeService';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ErrorMessage } from '@/components/common/ErrorMessage';
import type { DonationType } from '@/types/donation.types';
import clsx from 'clsx';
import companyLogo from '@/assets/images/10001_logo.png';

// ── helpers ──────────────────────────────────────────────────────────────────

function todayISO(): string {
  return new Date().toISOString().slice(0, 10); // YYYY-MM-DD for <input type="date">
}

// ── sub-components ────────────────────────────────────────────────────────────

interface PledgeTypePillProps {
  donationType: DonationType;
  selected: boolean;
  onSelect: (dt: DonationType) => void;
}

function PledgeTypePill({ donationType, selected, onSelect }: PledgeTypePillProps) {
  return (
    <button
      type="button"
      onClick={() => onSelect(donationType)}
      className={clsx(
        'relative flex flex-col items-center justify-center rounded-xl border-2 px-4 py-4 text-center transition-all duration-200',
        'w-80 h-50 shrink-0 hover:shadow-md hover:scale-[1.02]',
        selected
          ? 'border-primary-600 bg-primary-600 text-white shadow-md'
          : 'border-gray-200 bg-white text-gray-800 hover:border-primary-400'
      )}
    >
      {selected && (
        <span className="absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full bg-white">
          <svg className="h-3.5 w-3.5 text-primary-600" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clipRule="evenodd"
            />
          </svg>
        </span>
      )}
      <span className="text-xl font-bold leading-snug">
        {donationType.detailedTextHeader ||
          donationType.shortDescription ||
          `Type ${donationType.donationType}`}
      </span>
      {donationType.detailedText && (
        <span
          className={clsx(
            'mt-1 text-wrap text-sm',
            selected ? 'text-primary-100' : 'text-gray-500'
          )}
        >
          {donationType.detailedText}
        </span>
      )}
    </button>
  );
}

// ── page ─────────────────────────────────────────────────────────────────────

interface FormState {
  pledgeBy: string;
  pledgeEmail: string;
  pledgePhoneNumber: string;
  amountPledged: string;
  datePledged: string;
}

type SubmitState = 'idle' | 'submitting' | 'success' | 'error';

export function PledgePage() {
  const { companyIdNumber, company } = useCompany();
  const { isAuthenticated, user, donor } = useAuth();

  const [donationTypes, setDonationTypes] = useState<DonationType[]>([]);
  const [selectedType, setSelectedType] = useState<DonationType | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<Error | null>(null);

  const [form, setForm] = useState<FormState>({
    pledgeBy: '',
    pledgeEmail: '',
    pledgePhoneNumber: '',
    amountPledged: '',
    datePledged: todayISO(),
  });

  const [submitState, setSubmitState] = useState<SubmitState>('idle');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [formError, setFormError] = useState<Partial<Record<keyof FormState | 'type', string>>>({});

  // ── Pre-fill form when donor data is available ───────────────────────────
  useEffect(() => {
    if (donor) {
      setForm((prev) => ({
        ...prev,
        pledgeBy: donor.completeName || prev.pledgeBy,
        pledgeEmail: donor.eMail || prev.pledgeEmail,
        pledgePhoneNumber: donor.phoneNumber || prev.pledgePhoneNumber,
      }));
    } else if (user && !donor) {
      setForm((prev) => ({
        ...prev,
        pledgeBy: user.name || prev.pledgeBy,
        pledgeEmail: user.email || prev.pledgeEmail,
      }));
    }
  }, [donor, user]);

  // ── Load pledge-enabled donation types ───────────────────────────────────
  useEffect(() => {
    const load = async () => {
      if (!companyIdNumber) return;
      try {
        setIsLoading(true);
        setLoadError(null);
        const data = await getDonationTypes(companyIdNumber, { showPledge: 1 });
        setDonationTypes(data.items);
      } catch (err) {
        setLoadError(err instanceof Error ? err : new Error('Failed to load pledge types'));
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [companyIdNumber]);

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleFieldChange = (field: keyof FormState, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    // Clear field error on change
    setFormError((prev) => ({ ...prev, [field]: undefined }));
  };

  const validate = (): boolean => {
    const errors: Partial<Record<keyof FormState | 'type', string>> = {};
    if (!selectedType) errors.type = 'Please select a pledge type.';
    if (!form.pledgeBy.trim()) errors.pledgeBy = 'Your name is required.';
    if (!form.amountPledged || Number(form.amountPledged) <= 0)
      errors.amountPledged = 'Please enter a valid pledge amount.';
    if (!form.datePledged) errors.datePledged = 'Pledge date is required.';
    if (!form.pledgeEmail.trim()) errors.pledgeEmail = 'Email address is required.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.pledgeEmail))
      errors.pledgeEmail = 'Please enter a valid email address.';
    setFormError(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitState('submitting');
    setSubmitError(null);

    try {
      await createPledge({
        companyId: companyIdNumber!,
        pledgeBy: form.pledgeBy.trim(),
        pledgeEmail: form.pledgeEmail.trim() || null,
        pledgePhoneNumber: form.pledgePhoneNumber.trim() || null,
        pledgeDonationType: selectedType!.donationType,
        amountPledged: parseFloat(form.amountPledged),
        datePledged: new Date(form.datePledged).toISOString(),
        donorId: donor?.donorId ?? null,
        statusCode: 1,
      });
      setSubmitState('success');
    } catch (err) {
      setSubmitState('error');
      setSubmitError(
        err instanceof Error ? err.message : 'Failed to record your pledge. Please try again.'
      );
    }
  };

  const handleReset = () => {
    setSelectedType(null);
    setForm({
      pledgeBy: donor?.completeName || user?.name || '',
      pledgeEmail: donor?.eMail || user?.email || '',
      pledgePhoneNumber: donor?.phoneNumber || '',
      amountPledged: '',
      datePledged: todayISO(),
    });
    setSubmitState('idle');
    setSubmitError(null);
    setFormError({});
  };

  // ── Render states ─────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" message="Loading pledge options..." />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-xl">
          <ErrorMessage error={loadError} />
        </div>
      </div>
    );
  }

  // ── Success screen ────────────────────────────────────────────────────────
  if (submitState === 'success') {
    return (
      <div className="flex flex-col items-center justify-center py-20 space-y-6 text-center px-4">
        <div className="flex h-20 w-20 items-center justify-center rounded-full bg-green-100">
          <svg
            className="h-10 w-10 text-green-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-gray-900">Pledge Recorded!</h2>
        <p className="text-gray-600 w-2/3">
          Thank you, <span className="font-semibold">{form.pledgeBy}</span>. Your pledge of{' '}
          <span className="font-semibold">${parseFloat(form.amountPledged).toFixed(2)}</span> for{' '}
          <span className="font-semibold">
            {selectedType?.description || selectedType?.shortName}
          </span>{' '}
          has been recorded. May Allah bless your generosity. Our team will review your pledge and
          may reach out if we have any questions. In the meantime, feel free to share your pledge
          with friends and family to encourage them to give as well!
        </p>
        <button
          onClick={handleReset}
          className="mt-4 rounded-lg bg-primary-600 px-6 py-2.5 text-sm font-semibold text-white hover:bg-primary-700 transition-colors"
        >
          Make Another Pledge
        </button>
      </div>
    );
  }

  // ── Main form ─────────────────────────────────────────────────────────────
  return (
    <div className="space-y-8">
      {/* Page header */}
      <div className="text-center">
        {company?.companyLogo && (
          <div className="flex justify-center mb-3">
            <img
              src={companyLogo}
              alt={company.companyName}
              className="h-20 w-20 rounded-b-lg object-contain shadow-sm border border-gray-100"
            />
          </div>
        )}
        <h1 className="text-2xl font-bold text-gray-900">Make a Pledge</h1>
        <p className="mt-1 text-gray-500 text-sm">
          Select a cause below and fill in your pledge details.
        </p>
      </div>

      {/* Donation type selector */}
      <div className="bg-primary-50 rounded-xl p-5 shadow-sm">
        <h2 className="mb-4 text-base font-semibold text-gray-700">I am pledging for…</h2>
        {donationTypes.length === 0 ? (
          <p className="text-sm text-gray-500">No pledge categories available at this time.</p>
        ) : (
          <div className="flex flex-wrap justify-center gap-3">
            {donationTypes.map((dt) => (
              <PledgeTypePill
                key={dt.donationType}
                donationType={dt}
                selected={selectedType?.donationType === dt.donationType}
                onSelect={setSelectedType}
              />
            ))}
          </div>
        )}
        {formError.type && <p className="mt-2 text-sm text-red-600">{formError.type}</p>}
      </div>

      {/* Pledge form */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <form onSubmit={handleSubmit} noValidate className="space-y-5">
          {/* Pledge By */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="pledgeBy">
              Your Name <span className="text-red-500">*</span>
            </label>
            <input
              id="pledgeBy"
              type="text"
              value={form.pledgeBy}
              onChange={(e) => handleFieldChange('pledgeBy', e.target.value)}
              placeholder="Full name"
              className={clsx(
                'w-full rounded-lg border px-4 py-2.5 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500',
                formError.pledgeBy ? 'border-red-400 focus:ring-red-400' : 'border-gray-300'
              )}
            />
            {formError.pledgeBy && (
              <p className="mt-1 text-xs text-red-600">{formError.pledgeBy}</p>
            )}
          </div>

          {/* Two-column: email + phone */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="pledgeEmail">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                id="pledgeEmail"
                type="email"
                value={form.pledgeEmail}
                onChange={(e) => handleFieldChange('pledgeEmail', e.target.value)}
                placeholder="you@example.com"
                className={clsx(
                  'w-full rounded-lg border px-4 py-2.5 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500',
                  formError.pledgeEmail ? 'border-red-400 focus:ring-red-400' : 'border-gray-300'
                )}
              />
              {formError.pledgeEmail && (
                <p className="mt-1 text-xs text-red-600">{formError.pledgeEmail}</p>
              )}
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="pledgePhone">
                Phone Number
              </label>
              <input
                id="pledgePhone"
                type="tel"
                value={form.pledgePhoneNumber}
                onChange={(e) => handleFieldChange('pledgePhoneNumber', e.target.value)}
                placeholder="(555) 000-0000"
                className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>

          {/* Two-column: amount + date */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Amount */}
            <div>
              <label
                className="block text-sm font-medium text-gray-700 mb-1"
                htmlFor="amountPledged"
              >
                Pledge Amount ($) <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-gray-400 text-sm">
                  $
                </span>
                <input
                  id="amountPledged"
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={form.amountPledged}
                  onChange={(e) => handleFieldChange('amountPledged', e.target.value)}
                  placeholder="0.00"
                  className={clsx(
                    'w-full rounded-lg border pl-7 pr-4 py-2.5 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500',
                    formError.amountPledged
                      ? 'border-red-400 focus:ring-red-400'
                      : 'border-gray-300'
                  )}
                />
              </div>
              {formError.amountPledged && (
                <p className="mt-1 text-xs text-red-600">{formError.amountPledged}</p>
              )}
            </div>

            {/* Date Pledged */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="datePledged">
                Date Pledged <span className="text-red-500">*</span>
              </label>
              <input
                id="datePledged"
                type="date"
                value={form.datePledged}
                onChange={(e) => handleFieldChange('datePledged', e.target.value)}
                className={clsx(
                  'w-full rounded-lg border px-4 py-2.5 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500',
                  formError.datePledged ? 'border-red-400 focus:ring-red-400' : 'border-gray-300'
                )}
              />
              {formError.datePledged && (
                <p className="mt-1 text-xs text-red-600">{formError.datePledged}</p>
              )}
            </div>
          </div>

          {/* Not authenticated reminder */}
          {!isAuthenticated && (
            <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800">
              <strong>Tip:</strong> Log in to automatically fill in your contact details.
            </div>
          )}

          {/* Pledge summary strip */}
          {selectedType && form.amountPledged && Number(form.amountPledged) > 0 && (
            <div className="rounded-lg bg-primary-50 border border-primary-200 px-4 py-3 text-sm text-primary-800">
              You are pledging{' '}
              <span className="font-semibold">${parseFloat(form.amountPledged).toFixed(2)}</span>{' '}
              for{' '}
              <span className="font-semibold">
                {selectedType.description || selectedType.shortName}
              </span>
              .
            </div>
          )}

          {/* Submit error */}
          {submitState === 'error' && submitError && (
            <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
              {submitError}
            </div>
          )}

          {/* Action buttons */}
          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={handleReset}
              className="rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Clear
            </button>
            <button
              type="submit"
              disabled={submitState === 'submitting'}
              className={clsx(
                'rounded-lg px-6 py-2.5 text-sm font-semibold text-white transition-colors',
                submitState === 'submitting'
                  ? 'bg-primary-400 cursor-not-allowed'
                  : 'bg-primary-600 hover:bg-primary-700'
              )}
            >
              {submitState === 'submitting' ? (
                <span className="flex items-center gap-2">
                  <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                  Recording Pledge…
                </span>
              ) : (
                'Record My Pledge'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
